import React, { useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { useAgent } from "@/lib/agentContext";
import { track } from "@/lib/track";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, ArrowRight, BadgeCheck, ShieldCheck, TrendingUp } from "lucide-react";

const ALL_LOCALITIES = [
  "Baner", "Balewadi", "Wakad", "Tathawade",
  "Kharadi", "Viman Nagar", "Wagholi",
];

export default function Sell() {
  const agent = useAgent();
  const [intent, setIntent] = useState("Sell");
  const [locality, setLocality] = useState(agent?.primary_locality || "");
  const [bhk, setBhk] = useState("");
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");

  const greeting = agent ? `Hi ${agent.name}, ` : "Hi, ";
  const summary = `${greeting}I'd like to ${intent.toLowerCase()} a property in Pune. Locality: ${locality || "—"} | Configuration: ${bhk || "—"} | Asking: ${price || "—"} | Notes: ${notes || "—"}`;
  const waNum = agent?.phone ? agent.phone.replace(/[^0-9]/g, "") : "919999999999";
  const waHref = `https://wa.me/${waNum}?text=${encodeURIComponent(summary)}`;
  const onWa = () => agent && track({ event_type: "wa_click", agent_slug: agent.slug, source: "microsite" });

  const inner = (
    <>
      <section className="bg-sg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20"
             style={{ backgroundImage: "url(https://images.pexels.com/photos/21835254/pexels-photo-21835254.jpeg)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="overline text-sg-gold mb-5">SELL · LEASE OUT</div>
          <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            {agent
              ? <>List with <span className="font-serif-italic text-sg-gold">{agent.name.split(" ")[0]}.</span></>
              : <>Sell smarter. <span className="font-serif-italic text-sg-gold">Route once.</span></>}
          </h1>
          <p className="mt-6 text-white/75 max-w-2xl">
            {agent
              ? `${agent.name} has closed deals across ${agent.primary_locality} and the wider ${agent.micro_market} corridor. Send your brief and you'll hear back the same day with a positioning recommendation.`
              : "One short brief. We hand it to a MahaCRITI-verified corridor specialist who knows what actually trades in your locality — and at what price."}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        <div className="bg-white border border-sg-line rounded-3xl p-8">
          <div className="overline text-sg-gold-deep mb-3">YOUR BRIEF</div>
          <h2 className="font-display text-3xl text-sg-navy mb-6">
            Tell {agent ? agent.name.split(" ")[0] : "us"} about your <span className="font-serif-italic text-sg-gold-deep">property.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="overline text-sg-gold-deep">Intent</Label>
              <Select value={intent} onValueChange={setIntent}>
                <SelectTrigger className="rounded-full border-sg-line" data-testid="sell-intent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sell">Sell outright</SelectItem>
                  <SelectItem value="Lease Out">Lease out (long-term rent)</SelectItem>
                  <SelectItem value="Get a valuation">Just get a valuation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="overline text-sg-gold-deep">Locality</Label>
              <Select value={locality} onValueChange={setLocality}>
                <SelectTrigger className="rounded-full border-sg-line" data-testid="sell-locality"><SelectValue placeholder="Choose your Pune locality" /></SelectTrigger>
                <SelectContent>
                  {ALL_LOCALITIES.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="overline text-sg-gold-deep">Configuration</Label>
              <Input value={bhk} onChange={(e) => setBhk(e.target.value)} placeholder="e.g., 2.5 BHK" className="rounded-full border-sg-line" data-testid="sell-bhk" />
            </div>
            <div className="space-y-2">
              <Label className="overline text-sg-gold-deep">Asking price</Label>
              <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g., 1.45 Cr (or 95 Lac)" className="rounded-full border-sg-line" data-testid="sell-price" />
            </div>
          </div>
          <div className="space-y-2 mt-4">
            <Label className="overline text-sg-gold-deep">Anything else worth knowing?</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Carpet area, floor, view, parking, society maintenance, tenancy status…" className="rounded-2xl border-sg-line" data-testid="sell-notes" />
          </div>
          <a href={waHref} target="_blank" rel="noreferrer" onClick={onWa} className="pill-solid inline-flex items-center gap-2 mt-8 h-12 px-6" data-testid="sell-wa-cta">
            <MessageCircle className="w-4 h-4" /> Send brief on WhatsApp <ArrowRight className="w-4 h-4 ml-1" />
          </a>
          <p className="mt-3 text-xs text-sg-mute">
            We don't store this form. The brief opens directly in {agent ? `${agent.name.split(" ")[0]}'s` : "WhatsApp"} — you stay in control of the conversation.
          </p>
        </div>

        <div className="space-y-4">
          <div className="sg-card-dark">
            <div className="overline text-sg-gold mb-2">WHY THIS WORKS</div>
            <h3 className="font-display text-2xl leading-tight">
              {agent ? <>One brief. One corridor specialist. <span className="font-serif-italic text-sg-gold">Zero portal spam.</span></> : <>One brief, one specialist, <span className="font-serif-italic text-sg-gold">zero portal spam.</span></>}
            </h3>
            <p className="text-sm text-white/75 mt-3">
              {agent
                ? `${agent.name.split(" ")[0]} doesn't broadcast your details. The brief stays in your WhatsApp thread.`
                : "We don't auction your details across 30 brokers. We route once, to the right MahaCRITI-verified agent for your locality."}
            </p>
          </div>
          {[
            { icon: <BadgeCheck className="w-4 h-4" />, h: agent ? "MahaCRITI verified" : "MahaCRITI-verified", c: agent ? `${agent.name} · Reg No: ${agent.maharera_number}` : "Every agent shows their RERA registration on their own branded portal." },
            { icon: <ShieldCheck className="w-4 h-4" />, h: "No data sale", c: "We don't sell your number. You stay in the WhatsApp thread." },
            { icon: <TrendingUp className="w-4 h-4" />, h: "Locality YoY", c: "Pricing benchmarks pulled from our market-insights feed." },
          ].map((p) => (
            <div key={p.h} className="sg-card-cream flex gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-white border border-sg-line flex items-center justify-center text-sg-gold-deep">{p.icon}</div>
              <div>
                <div className="font-display text-lg text-sg-navy">{p.h}</div>
                <p className="text-sm text-sg-mute mt-1">{p.c}</p>
              </div>
            </div>
          ))}
          <Link to={agent ? `/agent/${agent.slug}/market-insights` : "/market-insights"} className="pill-outline inline-flex items-center gap-2">
            See locality benchmarks <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );

  return agent ? inner : <PublicLayout>{inner}</PublicLayout>;
}
